// Obra › Clima › Planificador del mes: calendario con días aptos / no aptos.
//  - días pasados: clima real (+ si se trabajó, según la IA de personas, cuando la cámara tiene IA)
//  - próximos 16 días: pronóstico
//  - más adelante: lo que suele pasar en esa época en ese lugar (10 años del archivo de Open-Meteo)
//  - si la obra subió su cronograma (Datos de la obra › Cronograma), arriba: las próximas actividades según el Gantt
//    con el día que conviene para cada una (cronogramaClima.js). Sin cronograma, todo queda como antes.
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import R from './reglasClima';
import { historico, diezAnios, unir, mensajeClima } from './meteo';
import { actividadPorDia, fotoDelDia } from './fotosObra';
import { Seccion, Semaforo, Icono3D, Kpi, ESTADOS, MESES, mm, grados, Cargando } from './ui';
import { proximasActividades, recomendar, TIPO_TXT, TIPO_EMOJI } from './cronogramaClima';
import { getDatosObra } from '../../../../datosObra/datosObra.api';

const DOW_L = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
const finMes = (ym) => new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7), 0)).toISOString().slice(0, 10);
const mesMas = (ym, n) => { const d = new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7) - 1 + n, 1)); return d.toISOString().slice(0, 7); };
const pct = (p) => `${Math.round(p * 100)} %`;

export function useMes({ loc, ixFc, U, hoy, user, ym }) {
  const [st, setSt] = useState({ cargando: true });
  const [reintento, setReintento] = useState(0); // si falla el servicio del clima, se reintenta solo a los 30 s
  useEffect(() => {
    let vivo = true;
    setSt({ cargando: true });
    const desde = `${ym}-01`, hasta = finMes(ym);
    const ayer = R.addDias(hoy, -1);
    const pasado = desde <= ayer ? historico(loc, desde, hasta < ayer ? hasta : ayer, hoy) : Promise.resolve(null);
    Promise.all([pasado, diezAnios(loc, hoy).catch(() => null)]).then(([h, diez]) => {
      if (!vivo) return;
      setSt({ cargando: false, ix: unir(h, ixFc), diez, clim: diez ? R.climatologia(diez, U) : null, prom: diez ? R.promedioMes(diez, +ym.slice(5, 7), U) : null });
    }).catch((e) => { if (!vivo) return; setSt({ cargando: false, error: mensajeClima(e) }); setTimeout(() => vivo && setReintento((n) => n + 1), 30000); });
    return () => { vivo = false; };
  }, [loc, ixFc, hoy, ym, U, reintento]);
  // la actividad de la IA llega aparte (puede tardar)
  const [ia, setIa] = useState(undefined);
  useEffect(() => {
    let vivo = true;
    setIa(undefined);
    const desde = `${ym}-01`, hasta = finMes(ym);
    if (desde > hoy) { setIa(null); return undefined; }
    actividadPorDia(user, desde, hasta < hoy ? hasta : hoy).then((m) => vivo && setIa(m)).catch(() => vivo && setIa(null));
    return () => { vivo = false; };
  }, [user?.identifier, user?.customerId, ym, hoy]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...st, ia };
}

/** Evalúa cada día del mes con la mejor fuente disponible. */
export function diasDelMes(ym, ix, clim, U, hoy, ultimoPron) {
  const out = [];
  for (let f = `${ym}-01`; f <= finMes(ym); f = R.addDias(f, 1)) {
    const laborable = U.diasLaborales.includes(R.diaIso(f));
    let fuente = f < hoy ? 'real' : f <= ultimoPron ? 'pron' : 'clim';
    let e = null, c = null;
    if (fuente !== 'clim') {
      e = R.evaluarDia(ix, f, U);
      if (!e.estado) { fuente = f < hoy ? 'sin' : 'clim'; e = null; }
    }
    if (fuente === 'clim') c = clim ? clim.get(f.slice(5)) : null;
    out.push({ fecha: f, laborable, fuente, e, c, hoy: f === hoy });
  }
  return out;
}

export function indicadoresMes(dias, prom, ia) {
  const hab = dias.filter((d) => d.laborable);
  let aptos = 0, noAptos = 0, noEstimado = 0;
  hab.forEach((d) => {
    if (d.e) { if (d.e.estado === 'no') noAptos += 1; else aptos += 1; } else if (d.c) { aptos += 1 - d.c.pNoApto; noEstimado += d.c.pNoApto; }
  });
  const pasados = hab.filter((d) => d.fuente === 'real' && d.e);
  const perdidosLluvia = pasados.filter((d) => d.e.estado === 'no' && d.e.motivos.some((m) => m.estado === 'no' && (m.tipo === 'lluvia' || m.tipo === 'tormenta'))).length;
  const mmReal = dias.filter((d) => d.fuente === 'real' && d.e).reduce((s, d) => s + (d.e.datos.mmDia || 0), 0);
  const mmPron = dias.filter((d) => d.fuente === 'pron' && d.e).reduce((s, d) => s + (d.e.datos.mmDia || 0), 0);
  const mmClim = dias.filter((d) => d.fuente === 'clim' && d.c).reduce((s, d) => s + d.c.mmMedio, 0);
  let cruce = null;
  if (ia && ia.size) {
    const conDato = pasados.filter((d) => ia.has(d.fecha)); // días sin fotos no cuentan (la cámara pudo estar apagada)
    cruce = {
      trabajados: conDato.filter((d) => ia.get(d.fecha)?.trabajo).length,
      noAptosTrabajados: conDato.filter((d) => d.e.estado === 'no' && ia.get(d.fecha)?.trabajo).length,
      aptosSinActividad: conDato.filter((d) => d.e.estado !== 'no' && !ia.get(d.fecha)?.trabajo).length,
      pasados: conDato.length,
    };
  }
  return { habiles: hab.length, aptos: Math.round(aptos), noTotal: Math.round(noAptos + noEstimado), noAptosConocidos: noAptos, perdidosLluvia, mmReal, mmPron, mmClim, prom, cruce };
}

function Celda({ d, ia, sel, onSel }) {
  const E = d.e ? ESTADOS[d.e.estado] : null;
  const p = d.c ? d.c.pNoApto : null;
  const fondo = E ? E.suave : p != null ? (p >= 0.35 ? '#ffe4e6' : p >= 0.15 ? '#fef3c7' : '#ecfdf5') : '#f9fafb';
  const kind = d.e ? R.tipoTiempo(d.e.datos.code, d.e.datos.mmDia, false) : null;
  const act = ia && ia.get(d.fecha);
  return (
    <button type="button" onClick={() => onSel(d.fecha)} aria-pressed={sel}
      className={`relative min-h-[64px] sm:min-h-[96px] rounded-xl border text-left p-1 sm:p-1.5 flex flex-col transition-shadow hover:shadow-md ${sel ? 'ring-2 ring-blue-500 border-blue-300' : d.hoy ? 'border-gray-900 border-2' : 'border-white'} ${!d.laborable ? 'opacity-55' : ''}`}
      style={{ background: fondo }}>
      {d.fuente === 'clim' && <span className="absolute inset-0 rounded-xl clima-rayas pointer-events-none" />}
      <div className="relative flex items-start justify-between gap-0.5">
        <span className={`text-[11px] sm:text-sm font-bold ${d.hoy ? 'text-white bg-gray-900 rounded-full px-1.5' : 'text-gray-800'}`}>{Number(d.fecha.slice(8))}</span>
        {kind && <Icono3D kind={kind} size={22} className="hidden sm:block" />}
        {E && <span className="sm:hidden text-[10px]" aria-hidden="true">{E.emoji}</span>}
      </div>
      <div className="relative mt-auto space-y-0.5">
        {d.e && d.e.datos.mmDia >= 1 && <p className="hidden sm:block text-[10px] font-semibold text-sky-800">💧 {mm(d.e.datos.mmDia)} mm</p>}
        {d.c && <p className="text-[9px] sm:text-[10px] font-semibold text-gray-600 leading-tight" title="Probabilidad de que no se pueda trabajar, según los últimos 10 años">{pct(d.c.pNoApto)}<span className="hidden sm:inline"> no apto</span></p>}
        {act && d.laborable && <p className={`hidden sm:block text-[10px] font-semibold ${act.trabajo ? 'text-gray-700' : 'text-rose-700'}`}>{act.trabajo ? `👷 ${act.pico}` : 'sin actividad'}</p>}
        {E && <p className="hidden sm:block text-[10px] font-semibold" style={{ color: E.hex }}>{E.txt}</p>}
      </div>
    </button>
  );
}

function Detalle({ d, ia, user, U }) {
  const [foto, setFoto] = useState(null);
  useEffect(() => { setFoto(null); }, [d.fecha]);
  const act = ia && ia.get(d.fecha);
  const FUENTE = { real: 'Clima real', pron: 'Pronóstico', clim: 'Lo que suele pasar (últimos 10 años)', sin: 'Sin datos' };
  const verFoto = async () => {
    setFoto({ cargando: true });
    const lluv = d.e ? (d.e.datos.mmJ > 0 ? 11 : 12) : 12;
    setFoto((await fotoDelDia(user, d.fecha, lluv).catch(() => null)) || { error: true });
  };
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 clima-in">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-base font-semibold text-gray-900">{R.mayuscula(R.fechaLarga(d.fecha))}</p>
          <p className="text-xs text-gray-500">{FUENTE[d.fuente]}{!d.laborable ? ' · no se trabaja' : ''}</p>
        </div>
        {d.e && <Semaforo estado={d.e.estado} />}
      </div>
      {d.e && (
        <>
          <p className="text-sm text-gray-700 mt-2">Máx {grados(d.e.datos.tmaxDia ?? d.e.datos.tmax)} · mín {grados(d.e.datos.tminDia ?? d.e.datos.tmin)} · lluvia {mm(d.e.datos.mmDia)} mm ({mm(d.e.datos.mmJ)} en horario de obra) · ráfagas {Math.round(d.e.datos.rafaga ?? 0)} km/h</p>
          <ul className="mt-2 space-y-1">
            {d.e.motivos.length ? d.e.motivos.map((m) => <li key={m.texto} className="text-sm text-gray-800 flex gap-2"><span aria-hidden="true">{ESTADOS[m.estado].emoji}</span>{m.texto}</li>) : <li className="text-sm text-gray-800">✅ Buen día para trabajar</li>}
          </ul>
        </>
      )}
      {d.c && (
        <p className="text-sm text-gray-700 mt-2">
          En los últimos 10 años, alrededor de esta fecha llovió (1 mm o más) el <b>{pct(d.c.pLluvia)}</b> de los días y <b>{pct(d.c.pNoApto)}</b> no se pudo trabajar por el clima. Lluvia media: {mm(d.c.mmMedio)} mm por día.
        </p>
      )}
      {act && (
        <p className="text-sm text-gray-700 mt-2">👷 {act.trabajo ? <>Se trabajó: hasta <b>{act.pico}</b> personas a la vez, {Math.round(act.horasHombre)} horas-hombre.</> : <b className="text-rose-700">No se registró actividad en la obra.</b>}</p>
      )}
      {d.fuente === 'real' && user?.identifier && (
        <div className="mt-3">
          {!foto && <button type="button" onClick={verFoto} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline"><Camera className="w-4 h-4" />Ver la obra ese día</button>}
          {foto?.cargando && <p className="text-xs text-gray-500">Buscando la foto…</p>}
          {foto?.error && <p className="text-xs text-gray-500">No hay fotos de ese día.</p>}
          {foto?.url && <figure><img src={foto.url} alt="La obra ese día" className="rounded-xl w-full max-h-72 object-cover" /><figcaption className="text-[11px] text-gray-500 mt-1">{new Date(foto.ms - 3 * 3600e3).toISOString().slice(11, 16)} h</figcaption></figure>}
        </div>
      )}
    </div>
  );
}

/** El cronograma guardado en Datos de la obra (null si no hay o no se pudo leer: el Planificador sigue igual). */
export function useCronograma(feedId) {
  const [c, setC] = useState(null);
  useEffect(() => {
    let vivo = true;
    setC(null);
    if (!feedId) return undefined;
    getDatosObra(feedId).then((f) => vivo && setC(f?.cronograma || null)).catch(() => vivo && setC(null));
    return () => { vivo = false; };
  }, [feedId]);
  return c;
}

const TONO = { ok: 'border-emerald-200 bg-emerald-50/60', mover: 'border-amber-300 bg-amber-50', 'sin-pronostico': 'border-gray-200 bg-gray-50', 'sin-dias': 'border-gray-200 bg-gray-50' };

/** "Próximas actividades de tu cronograma": lo que según el Gantt toca ahora, con el día que conviene. */
export function ProximasActividades({ cronograma, ixFc, U, hoy, ultimoPron }) {
  const recs = useMemo(() => proximasActividades(cronograma, hoy).map((a) => recomendar(a, ixFc, U, hoy, ultimoPron)), [cronograma, ixFc, U, hoy, ultimoPron]);
  if (!cronograma || cronograma.estado !== 'listo') return null;
  return (
    <Seccion titulo="📋 Próximas actividades de tu cronograma" sub={`Según el cronograma que subiste (${cronograma.archivoNombre}) y el pronóstico de los próximos días`}>
      {recs.length ? (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {recs.map((r) => (
            <li key={`${r.act.nombre}-${r.act.inicio}`} className={`rounded-xl border px-3.5 py-3 ${TONO[r.estado] || TONO.ok}`}>
              <div className="flex items-start gap-2.5">
                <span className="text-xl leading-none mt-0.5" aria-hidden="true">{TIPO_EMOJI[r.act.tipo] || '📌'}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 leading-snug">{r.act.nombre}<span className="font-normal text-gray-600">, previsto {r.rango}</span></p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{TIPO_TXT[r.act.tipo] || r.act.tipo}{r.enCurso ? ' · en curso' : ''}</p>
                  <p className={`text-sm mt-1.5 leading-snug ${r.estado === 'mover' ? 'text-amber-900 font-medium' : 'text-gray-700'}`}>{r.estado === 'mover' ? '⚠️ ' : r.estado === 'ok' ? '✅ ' : ''}{r.texto}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-600">En las próximas 2 semanas el cronograma no tiene actividades que dependan del clima.</p>
      )}
      <p className="text-[11px] text-gray-500 mt-2">Es una guía con el pronóstico y tus criterios: la decisión final es del director de obra. Para cambiar el cronograma, subí uno nuevo en Obra › Datos de la obra.</p>
    </Seccion>
  );
}

export default function Planificador({ loc, ixFc, U, hoy, user, onUmbrales }) {
  const cronograma = useCronograma(user?.feedID);
  const [ym, setYm] = useState(hoy.slice(0, 7));
  const { cargando, error, ix, clim, prom, ia } = useMes({ loc, ixFc, U, hoy, user, ym });
  const [sel, setSel] = useState(null);
  const ultimoPron = ixFc.dias.length ? ixFc.dias[ixFc.dias.length - 1].fecha : hoy;
  const dias = useMemo(() => (ix ? diasDelMes(ym, ix, clim, U, hoy, ultimoPron) : []), [ym, ix, clim, U, hoy, ultimoPron]);
  const K = useMemo(() => indicadoresMes(dias, prom, ia), [dias, prom, ia]);
  const lunes = dias.length ? (R.diaIso(dias[0].fecha) - 1) : 0;
  const nombreMes = `${MESES[+ym.slice(5, 7) - 1]} ${ym.slice(0, 4)}`;
  const elegido = dias.find((d) => d.fecha === sel);
  const minYm = mesMas(hoy.slice(0, 7), -12), maxYm = mesMas(hoy.slice(0, 7), 6);
  const diff = K.prom ? K.noTotal - K.prom.noAptoHabiles : null;

  return (
    <div className="space-y-4 clima-in">
      <ProximasActividades cronograma={cronograma} ixFc={ixFc} U={U} hoy={hoy} ultimoPron={ultimoPron} />
      <Seccion
        titulo={<span className="flex items-center gap-2">
          <button type="button" aria-label="Mes anterior" disabled={ym <= minYm} onClick={() => { setYm(mesMas(ym, -1)); setSel(null); }} className="p-1 rounded-lg hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="w-5 h-5" /></button>
          <span className="capitalize min-w-[9.5rem] text-center">{nombreMes}</span>
          <button type="button" aria-label="Mes siguiente" disabled={ym >= maxYm} onClick={() => { setYm(mesMas(ym, 1)); setSel(null); }} className="p-1 rounded-lg hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
        </span>}
        sub="Organizá el mes: lo que pasó, lo que viene y lo que suele pasar en esta época"
        right={<>
          {ym !== hoy.slice(0, 7) && <button type="button" onClick={() => setYm(hoy.slice(0, 7))} className="text-xs font-semibold text-gray-700 border border-gray-300 rounded-lg px-2.5 py-1 hover:bg-gray-50">Este mes</button>}
          <button type="button" onClick={onUmbrales} className="text-xs font-semibold text-blue-700 hover:underline">Ajustar criterios</button>
        </>}
      >
        {cargando && <Cargando txt="Armando el mes…" />}
        {error && <p className="text-sm text-rose-700">{error}</p>}
        {!cargando && !error && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
              <Kpi tono="verde" grande label="Días aptos estimados" valor={<>{K.aptos}<span className="text-sm font-semibold text-gray-500"> de {K.habiles}</span></>} sub="días hábiles en los que se puede trabajar (aptos o con cuidado)" />
              <Kpi tono="rojo" grande label="Perdidos por lluvia" valor={K.perdidosLluvia} sub={ym < hoy.slice(0, 7) ? 'días hábiles del mes' : ym === hoy.slice(0, 7) ? 'días hábiles en lo que va del mes' : 'el mes todavía no empezó'} />
              <Kpi tono="ambar" grande label="No aptos en el mes" valor={<>{K.noTotal}<span className="text-sm font-semibold text-gray-500"> {K.prom ? `vs ${K.prom.noAptoHabiles.toFixed(1).replace('.', ',')}` : ''}</span></>}
                sub={K.prom ? (Math.abs(diff) < 1 ? `Normal para ${MESES[+ym.slice(5, 7) - 1]} (promedio de ${K.prom.anios} años)` : diff > 0 ? `${Math.round(diff)} más que el promedio de ${K.prom.anios} años` : `${Math.round(-diff)} menos que el promedio de ${K.prom.anios} años`) : 'real + pronóstico + estimado'} />
              <Kpi tono="azul" grande label={K.mmClim > 0 ? 'Lluvia del mes (estimada)' : 'Lluvia del mes'} valor={<>{Math.round(K.mmReal + K.mmPron + K.mmClim)}<span className="text-sm font-semibold text-gray-500"> mm</span></>} sub={K.prom ? `Promedio de ${MESES[+ym.slice(5, 7) - 1]}: ${Math.round(K.prom.mm)} mm · ${K.prom.diasLluvia.toFixed(1).replace('.', ',')} días de lluvia` : `${Math.round(K.mmReal)} mm caídos + ${Math.round(K.mmPron)} pronosticados`} />
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
              {DOW_L.map((d, i) => <div key={d} className={`text-center text-[11px] font-semibold uppercase ${U.diasLaborales.includes(i + 1) ? 'text-gray-500' : 'text-gray-300'}`}>{d}</div>)}
              {Array.from({ length: lunes }, (_, i) => <div key={'v' + i} />)}
              {dias.map((d) => <Celda key={d.fecha} d={d} ia={ia} sel={sel === d.fecha} onSel={(f) => setSel(sel === f ? null : f)} />)}
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-gray-600 mt-3">
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: ESTADOS.apto.suave }} />Apto</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: ESTADOS.cuidado.suave }} />Con cuidado</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: ESTADOS.no.suave }} />No apto</span>
              <span className="inline-flex items-center gap-1.5"><span className="relative w-3 h-3 rounded bg-amber-100 overflow-hidden"><span className="absolute inset-0 clima-rayas" /></span>Estimado con 10 años de historia (% = probabilidad de no poder trabajar)</span>
              {ia && <span className="inline-flex items-center gap-1.5">👷 personas en la obra (pico del día)</span>}
            </div>
            {ia === undefined && <p className="text-[11px] text-gray-400 mt-1">Buscando la actividad de la obra…</p>}

            {K.cruce && (
              <div className="mt-4 rounded-xl bg-indigo-50 border border-indigo-100 px-4 py-3 text-sm text-indigo-950">
                <p className="font-semibold mb-1">Clima y trabajo en la obra este mes</p>
                <p>Se trabajó <b>{K.cruce.trabajados}</b> de {K.cruce.pasados} días hábiles.
                  {K.cruce.noAptosTrabajados > 0 && <> En <b>{K.cruce.noAptosTrabajados}</b> {K.cruce.noAptosTrabajados === 1 ? 'día no apto se trabajó' : 'días no aptos se trabajó'} igual.</>}
                  {K.cruce.aptosSinActividad > 0 && <> Hubo <b>{K.cruce.aptosSinActividad}</b> {K.cruce.aptosSinActividad === 1 ? 'día apto' : 'días aptos'} sin actividad: no fue por el clima.</>}
                </p>
              </div>
            )}
            {elegido && <div className="mt-4"><Detalle d={elegido} ia={ia} user={user} U={U} /></div>}
          </>
        )}
      </Seccion>
    </div>
  );
}
